import type { ComponentType } from 'react'
import { EbayLogo } from '../../../components/brand/EbayLogo'

export type StoreProviderId =
  | 'etsy'
  | 'shopify'
  | 'amazon'
  | 'woocommerce'
  | 'tiktok'
  | 'wix'
  | 'squarespace'
  | 'bigcommerce'
  | 'ebay'

export type StoreProviderDefinition = {
  id: StoreProviderId
  name: string
  availability: 'connectable' | 'coming-soon'
  mark: string
  markClassName: string
  /**
   * Optional real brand logo. When set, the shared ProviderMark renders this
   * component inside the chip (styled by markClassName) instead of the letter
   * mark — every surface that goes through ProviderMark picks it up.
   */
  Logo?: ComponentType<{ className?: string }>
  comparison: boolean
  recommended?: boolean
}

export const STORE_PROVIDERS: readonly StoreProviderDefinition[] = [
  {
    id: 'etsy',
    name: 'Etsy',
    availability: 'connectable',
    mark: 'E',
    markClassName: 'bg-[#f1641e] text-white font-serif',
    comparison: true,
    recommended: true,
  },
  {
    id: 'shopify',
    name: 'Shopify',
    availability: 'connectable',
    mark: 'S',
    markClassName: 'bg-[#95bf47] text-white',
    comparison: true,
    recommended: true,
  },
  {
    id: 'amazon',
    name: 'Amazon',
    availability: 'connectable',
    mark: 'a',
    markClassName: 'bg-[#111827] text-white',
    comparison: true,
  },
  {
    id: 'woocommerce',
    name: 'WooCommerce',
    availability: 'coming-soon',
    mark: 'woo',
    markClassName: 'bg-[#96588a] text-white text-[10px]',
    comparison: false,
  },
  {
    id: 'tiktok',
    name: 'TikTok Shop',
    availability: 'coming-soon',
    mark: '♪',
    markClassName: 'bg-black text-white',
    comparison: false,
  },
  {
    id: 'wix',
    name: 'Wix',
    availability: 'coming-soon',
    mark: 'WIX',
    markClassName: 'bg-slate-100 text-slate-900 text-[10px]',
    comparison: false,
  },
  {
    id: 'squarespace',
    name: 'Squarespace',
    availability: 'coming-soon',
    mark: 'S',
    markClassName: 'bg-slate-950 text-white',
    comparison: false,
  },
  {
    id: 'bigcommerce',
    name: 'BigCommerce',
    availability: 'coming-soon',
    mark: 'B',
    markClassName: 'bg-[#34313f] text-white',
    comparison: false,
  },
  {
    id: 'ebay',
    name: 'eBay',
    availability: 'connectable',
    mark: 'e',
    markClassName: 'bg-white text-[#e53238] ring-1 ring-slate-200',
    Logo: EbayLogo,
    comparison: false,
  },
]

export const CONNECTABLE_STORE_PROVIDERS = STORE_PROVIDERS.filter(
  (provider) => provider.availability === 'connectable',
)

export const COMING_SOON_STORE_PROVIDERS = STORE_PROVIDERS.filter(
  (provider) => provider.availability === 'coming-soon',
)

export function getStoreProvider(
  id: string,
): StoreProviderDefinition | undefined {
  return STORE_PROVIDERS.find((provider) => provider.id === id)
}
